import React, { useState, useEffect } from 'react';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon, 
  CheckIcon,
  XMarkIcon,
  DocumentTextIcon,
  CogIcon,
  StarIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconFilled } from '@heroicons/react/24/solid';
import { useApiRequest } from '../../hooks/useApiRequest';
import { contentAPI } from '../../api/contentApi';

const ChatGPTSettings = () => {
  const { loading, error, makeRequest } = useApiRequest();
  const [settings, setSettings] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editingSetting, setEditingSetting] = useState(null);
  const [newSetting, setNewSetting] = useState({
    name: '',
    description: '',
    system_prompt: 'Sen profesyonel bir blog yazarısın ve Türkçe içerik üretiyorsun.',
    model: 'gpt-4o-mini',
    max_tokens: 2000,
    temperature: 0.8,
    top_p: 0.9,
    frequency_penalty: 0.1,
    presence_penalty: 0.1,
    is_active: true,
    is_default: false
  });

  const models = [
    { value: 'gpt-3.5-turbo', label: 'GPT-3.5 Turbo' },
    { value: 'gpt-4', label: 'GPT-4' },
    { value: 'gpt-4o', label: 'GPT-4o' },
    { value: 'gpt-4o-mini', label: 'GPT-4o Mini' }
  ];

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await makeRequest(() => contentAPI.getChatGPTSettings());
      
      // DRF pagination formatından results array'ini al
      if (response && response.results && Array.isArray(response.results)) {
        setSettings(response.results);
      } else if (Array.isArray(response)) {
        setSettings(response);
      } else {
        setSettings([]);
      }
    } catch (err) {
      // Silently handle error
      setSettings([]);
    }
  };

  const handleSaveSetting = async () => {
    try {
      const settingData = editingSetting ? editingSetting : newSetting;
      
      const response = editingSetting 
        ? await makeRequest(() => contentAPI.updateChatGPTSetting(editingSetting.id, settingData))
        : await makeRequest(() => contentAPI.createChatGPTSetting(settingData));
      
      // DRF response formatını kontrol et
      if (response && (response.id || response.name)) {
        // Başarılı kayıt - response direkt data
        await loadSettings();
        setIsEditing(false);
        setEditingSetting(null);
        setNewSetting({
          name: '',
          description: '',
          system_prompt: 'Sen profesyonel bir blog yazarısın ve Türkçe içerik üretiyorsun.',
          model: 'gpt-4o-mini',
          max_tokens: 2000,
          temperature: 0.8,
          top_p: 0.9,
          frequency_penalty: 0.1,
          presence_penalty: 0.1,
          is_active: true,
          is_default: false
        });
      }
    } catch (err) {
      // Silently handle error
    }
  };

  const handleEditSetting = async (setting) => {
    try {
      // Liste endpoint'i kısıtlı alan döndürüyor olabilir; detayını çek
      const full = await makeRequest(() => contentAPI.getChatGPTSettingById(setting.id));
      if (full && full.id) {
        setEditingSetting(full);
      } else {
        // Yine de eldeki veriyi kullan
        setEditingSetting(setting);
      }
      setIsEditing(true);
    } catch (e) {
      // Hata durumunda mevcut objeyi kullanarak aç
      setEditingSetting(setting);
      setIsEditing(true);
    }
  };

  const handleDeleteSetting = async (settingId) => {
    if (!window.confirm('Bu ayarı silmek istediğinizden emin misiniz?')) return;

    try {
      const response = await makeRequest(() => contentAPI.deleteChatGPTSetting(settingId));
      
      // DRF DELETE genellikle 204 No Content döndürür
      await loadSettings();
    } catch (err) {
      // Silently handle error
    }
  };

  const handleSetDefault = async (settingId) => {
    try {
      const response = await makeRequest(() => 
        contentAPI.partialUpdateChatGPTSetting(settingId, { is_default: true })
      );
      
      // DRF PATCH response formatını kontrol et
      if (response && (response.id || response.name)) {
        await loadSettings();
      }
    } catch (err) {
      // Silently handle error
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditingSetting(null);
    setNewSetting({
      name: '',
      description: '',
      system_prompt: 'Sen profesyonel bir blog yazarısın ve Türkçe içerik üretiyorsun.',
      model: 'gpt-4o-mini',
      max_tokens: 2000,
      temperature: 0.8,
      top_p: 0.9,
      frequency_penalty: 0.1,
      presence_penalty: 0.1,
      is_active: true,
      is_default: false
    });
  };

  const getModelColor = (model) => {
    const colors = {
      'gpt-3.5-turbo': 'bg-green-100 text-green-800',
      'gpt-4': 'bg-blue-100 text-blue-800',
      'gpt-4o': 'bg-purple-100 text-purple-800',
      'gpt-4o-mini': 'bg-orange-100 text-orange-800'
    };
    return colors[model] || colors['gpt-4o-mini'];
  };

  const getModelLabel = (model) => {
    const modelObj = models.find(m => m.value === model);
    return modelObj ? modelObj.label : model;
  };

  const getTemperatureDescription = (temp) => {
    if (temp <= 0.3) return 'Çok Deterministik';
    if (temp <= 0.7) return 'Dengeli';
    if (temp <= 1.0) return 'Yaratıcı';
    if (temp <= 1.5) return 'Çok Yaratıcı';
    return 'Aşırı Yaratıcı';
  };

  const getTopPDescription = (topP) => {
    if (topP <= 0.3) return 'Çok Odaklı';
    if (topP <= 0.7) return 'Dengeli';
    if (topP <= 0.9) return 'Çeşitli';
    return 'Çok Çeşitli';
  };

  const getPenaltyDescription = (penalty) => {
    if (penalty <= -1) return 'Çok Pozitif';
    if (penalty <= -0.5) return 'Pozitif';
    if (penalty <= 0.5) return 'Nötr';
    if (penalty <= 1) return 'Negatif';
    return 'Çok Negatif';
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
              <CogIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                ChatGPT Ayarları
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                Blog yazısı oluştururken kullanılacak ChatGPT ayarlarını yönetin
              </p>
              <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  <strong>💡 Token Bilgisi:</strong> 1 token ≈ 0.6 kelime (Türkçe için). 
                  Örneğin 2000 token ≈ 1200 kelime blog yazısı oluşturur.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Add/Edit Setting Form */}
        {isEditing && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {editingSetting ? 'Ayarı Düzenle' : 'Yeni Ayar Ekle'}
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Ayar Adı
                </label>
                <input
                  type="text"
                  value={editingSetting ? editingSetting.name : newSetting.name}
                  onChange={(e) => {
                    if (editingSetting) {
                      setEditingSetting({ ...editingSetting, name: e.target.value });
                    } else {
                      setNewSetting({ ...newSetting, name: e.target.value });
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Örn: SEO Optimized Blog"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Model
                </label>
                <select
                  value={editingSetting ? editingSetting.model : newSetting.model}
                  onChange={(e) => {
                    if (editingSetting) {
                      setEditingSetting({ ...editingSetting, model: e.target.value });
                    } else {
                      setNewSetting({ ...newSetting, model: e.target.value });
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  {models.map(model => (
                    <option key={model.value} value={model.value}>
                      {model.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Açıklama
                </label>
                <input
                  type="text"
                  value={editingSetting ? editingSetting.description : newSetting.description}
                  onChange={(e) => {
                    if (editingSetting) {
                      setEditingSetting({ ...editingSetting, description: e.target.value });
                    } else {
                      setNewSetting({ ...newSetting, description: e.target.value });
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Bu ayarın ne için kullanıldığını açıklayın..."
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  System Prompt
                </label>
                <textarea
                  value={editingSetting ? editingSetting.system_prompt : newSetting.system_prompt}
                  onChange={(e) => {
                    if (editingSetting) {
                      setEditingSetting({ ...editingSetting, system_prompt: e.target.value });
                    } else {
                      setNewSetting({ ...newSetting, system_prompt: e.target.value });
                    }
                  }}
                  rows={6}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="ChatGPT'ye nasıl davranması gerektiğini söyleyen prompt..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Max Tokens
                  <span className="text-xs text-gray-500 ml-2">
                    (~{Math.round((editingSetting ? editingSetting.max_tokens : newSetting.max_tokens) * 0.6)} kelime)
                  </span>
                </label>
                <div className="space-y-2">
                  <input
                    type="range"
                    min="100"
                    max="4000"
                    step="50"
                    value={editingSetting ? editingSetting.max_tokens : newSetting.max_tokens}
                    onChange={(e) => {
                      const value = parseInt(e.target.value);
                      if (editingSetting) {
                        setEditingSetting({ ...editingSetting, max_tokens: value });
                      } else {
                        setNewSetting({ ...newSetting, max_tokens: value });
                      }
                    }}
                    className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
                  />
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>100</span>
                    <span className="font-medium text-blue-600">
                      {editingSetting ? editingSetting.max_tokens : newSetting.max_tokens}
                    </span>
                    <span>4000</span>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Temperature
                  <span className="text-xs text-gray-500 ml-2">
                    ({editingSetting ? editingSetting.temperature : newSetting.temperature} - {getTemperatureDescription(editingSetting ? editingSetting.temperature : newSetting.temperature)})
                  </span>
                </label>
                <div className="space-y-2">
                  <input
                    type="range"
                    min="0"
                    max="2"
                    step="0.1"
                    value={editingSetting ? editingSetting.temperature : newSetting.temperature}
                    onChange={(e) => {
                      const value = parseFloat(e.target.value);
                      if (editingSetting) {
                        setEditingSetting({ ...editingSetting, temperature: value });
                      } else {
                        setNewSetting({ ...newSetting, temperature: value });
                      }
                    }}
                    className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
                  />
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>0.0 (Deterministik)</span>
                    <span className="font-medium text-blue-600">
                      {editingSetting ? editingSetting.temperature : newSetting.temperature}
                    </span>
                    <span>2.0 (Çok Yaratıcı)</span>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Top P
                  <span className="text-xs text-gray-500 ml-2">
                    ({editingSetting ? editingSetting.top_p : newSetting.top_p} - {getTopPDescription(editingSetting ? editingSetting.top_p : newSetting.top_p)})
                  </span>
                </label>
                <div className="space-y-2">
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={editingSetting ? editingSetting.top_p : newSetting.top_p}
                    onChange={(e) => {
                      const value = parseFloat(e.target.value);
                      if (editingSetting) {
                        setEditingSetting({ ...editingSetting, top_p: value });
                      } else {
                        setNewSetting({ ...newSetting, top_p: value });
                      }
                    }}
                    className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
                  />
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>0.0 (Odaklı)</span>
                    <span className="font-medium text-blue-600">
                      {editingSetting ? editingSetting.top_p : newSetting.top_p}
                    </span>
                    <span>1.0 (Çeşitli)</span>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Frequency Penalty
                  <span className="text-xs text-gray-500 ml-2">
                    ({editingSetting ? editingSetting.frequency_penalty : newSetting.frequency_penalty} - {getPenaltyDescription(editingSetting ? editingSetting.frequency_penalty : newSetting.frequency_penalty)})
                  </span>
                </label>
                <div className="space-y-2">
                  <input
                    type="range"
                    min="-2"
                    max="2"
                    step="0.1"
                    value={editingSetting ? editingSetting.frequency_penalty : newSetting.frequency_penalty}
                    onChange={(e) => {
                      const value = parseFloat(e.target.value);
                      if (editingSetting) {
                        setEditingSetting({ ...editingSetting, frequency_penalty: value });
                      } else {
                        setNewSetting({ ...newSetting, frequency_penalty: value });
                      }
                    }}
                    className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
                  />
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>-2.0 (Tekrar Sever)</span>
                    <span className="font-medium text-blue-600">
                      {editingSetting ? editingSetting.frequency_penalty : newSetting.frequency_penalty}
                    </span>
                    <span>2.0 (Tekrar Kaçınır)</span>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Presence Penalty
                  <span className="text-xs text-gray-500 ml-2">
                    ({editingSetting ? editingSetting.presence_penalty : newSetting.presence_penalty} - {getPenaltyDescription(editingSetting ? editingSetting.presence_penalty : newSetting.presence_penalty)})
                  </span>
                </label>
                <div className="space-y-2">
                  <input
                    type="range"
                    min="-2"
                    max="2"
                    step="0.1"
                    value={editingSetting ? editingSetting.presence_penalty : newSetting.presence_penalty}
                    onChange={(e) => {
                      const value = parseFloat(e.target.value);
                      if (editingSetting) {
                        setEditingSetting({ ...editingSetting, presence_penalty: value });
                      } else {
                        setNewSetting({ ...newSetting, presence_penalty: value });
                      }
                    }}
                    className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
                  />
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>-2.0 (Konuya Odaklı)</span>
                    <span className="font-medium text-blue-600">
                      {editingSetting ? editingSetting.presence_penalty : newSetting.presence_penalty}
                    </span>
                    <span>2.0 (Konu Değiştirir)</span>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={editingSetting ? editingSetting.is_active : newSetting.is_active}
                    onChange={(e) => {
                      if (editingSetting) {
                        setEditingSetting({ ...editingSetting, is_active: e.target.checked });
                      } else {
                        setNewSetting({ ...newSetting, is_active: e.target.checked });
                      }
                    }}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                    Aktif
                  </span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={editingSetting ? editingSetting.is_default : newSetting.is_default}
                    onChange={(e) => {
                      if (editingSetting) {
                        setEditingSetting({ ...editingSetting, is_default: e.target.checked });
                      } else {
                        setNewSetting({ ...newSetting, is_default: e.target.checked });
                      }
                    }}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                    Varsayılan
                  </span>
                </label>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={handleCancelEdit}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                <XMarkIcon className="w-4 h-4 mr-2 inline" />
                İptal
              </button>
              <button
                onClick={handleSaveSetting}
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                <CheckIcon className="w-4 h-4 mr-2 inline" />
                {loading ? 'Kaydediliyor...' : 'Kaydet'}
              </button>
            </div>
          </div>
        )}

        {/* Settings List */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Mevcut Ayarlar
              </h2>
              {!isEditing && (
                <button
                  onClick={() => setIsEditing(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <PlusIcon className="w-4 h-4 mr-2 inline" />
                  Yeni Ayar
                </button>
              )}
            </div>
          </div>

          <div className="p-6">
            {settings.length === 0 ? (
              <div className="text-center py-12">
                <CogIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500 dark:text-gray-400">
                  Henüz hiç ayar tanımlanmamış.
                </p>
                <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                  İlk ayarınızı eklemek için "Yeni Ayar" butonuna tıklayın.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {Array.isArray(settings) && settings.map((setting) => (
                  <div
                    key={setting.id}
                    className={`p-4 rounded-lg border ${
                      setting.is_active
                        ? 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
                        : 'border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900'
                    } ${setting.is_default ? 'ring-2 ring-blue-500' : ''}`}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className={`font-semibold ${
                            setting.is_active
                              ? 'text-gray-900 dark:text-white'
                              : 'text-gray-500 dark:text-gray-400'
                          }`}>
                            {setting.name}
                          </h3>
                          <span className={`px-2 py-1 text-xs rounded-full ${getModelColor(setting.model)}`}>
                            {getModelLabel(setting.model)}
                          </span>
                          {setting.is_default && (
                            <span className="px-2 py-1 text-xs bg-yellow-100 text-yellow-600 rounded-full flex items-center gap-1">
                              <StarIconFilled className="w-3 h-3" />
                              Varsayılan
                            </span>
                          )}
                          {!setting.is_active && (
                            <span className="px-2 py-1 text-xs bg-red-100 text-red-600 rounded-full">
                              Pasif
                            </span>
                          )}
                        </div>
                        <p className={`text-sm mb-2 ${
                          setting.is_active
                            ? 'text-gray-600 dark:text-gray-300'
                            : 'text-gray-400 dark:text-gray-500'
                        }`}>
                          {setting.description}
                        </p>
                        <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1">
                          <p>Max Tokens: {setting.max_tokens} (~{Math.round(setting.max_tokens * 0.6)} kelime) | Temperature: {setting.temperature} | Top P: {setting.top_p}</p>
                          <p>Frequency Penalty: {setting.frequency_penalty} | Presence Penalty: {setting.presence_penalty}</p>
                        </div>
                      </div>
                      <div className="flex gap-2 ml-4">
                        {!setting.is_default && (
                          <button
                            onClick={() => handleSetDefault(setting.id)}
                            className="p-2 text-gray-400 hover:text-yellow-600 transition-colors"
                            title="Varsayılan Yap"
                          >
                            <StarIcon className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleEditSetting(setting)}
                          className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                          title="Düzenle"
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteSetting(setting.id)}
                          className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                          title="Sil"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatGPTSettings;